import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import {
  CalendarDays, ClipboardList, Monitor, Truck,
  Shield, Sparkles, Database, Settings as SettingsIcon, ArrowLeft, BarChart3, ScrollText, MapPin
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Link } from 'react-router-dom';
import BackupRestoreModal from './BackupRestoreModal';
import { canEditModule } from '../../App';

const NAV_TOP = [
  { to: '/routeops', label: 'Weekly Planner', icon: CalendarDays, testId: 'routeops-nav-planner', exact: true, moduleKey: 'routeops_planner' },
  { to: '/routeops/bookings', label: 'Bookings', icon: ClipboardList, testId: 'routeops-nav-bookings', moduleKey: 'routeops_bookings' },
  { to: '/routeops/terminal', label: 'Terminal', icon: Monitor, testId: 'routeops-nav-terminal', moduleKey: 'routeops_terminal' },
  { to: '/routeops/routes', label: 'Route Configuration', icon: Truck, testId: 'routeops-nav-routes', moduleKey: 'routeops_routes' },
  { to: '/routeops/reporting', label: 'Reporting', icon: BarChart3, testId: 'routeops-nav-reporting', moduleKey: 'routeops_reporting' },
  { to: '/routeops/audit', label: 'Audit Trail', icon: Shield, testId: 'routeops-nav-audit', moduleKey: 'routeops_audit' },
  { to: '/routeops/ai', label: 'AI Assistant', icon: Sparkles, testId: 'routeops-nav-ai', moduleKey: 'routeops_ai' },
  { to: '/routeops/changelog', label: 'Changelog', icon: ScrollText, testId: 'routeops-nav-changelog', moduleKey: 'routeops_changelog' },
  { to: '/routeops/driver-tracking', label: 'Driver Tracking', icon: MapPin, testId: 'routeops-nav-driver-tracking', moduleKey: 'routeops_driver_tracking' },
];

// Role-default module list mirrors backend. Used when user.allowed_modules is null.
const ROUTEOPS_MODULES_ALL = NAV_TOP.map(n => n.moduleKey).concat(['routeops_settings']);
const ROLE_DEFAULT_MODULES = {
  Admin: ROUTEOPS_MODULES_ALL,
  Manager: ROUTEOPS_MODULES_ALL,
  Supervisor: [],
  Driver: [],
};

function userHasModule(u, key) {
  if (!u) return false;
  if (Array.isArray(u.allowed_modules)) return u.allowed_modules.includes(key);
  return (ROLE_DEFAULT_MODULES[u.role] || []).includes(key);
}

function SideIcon({ to, label, icon: Icon, testId, exact, badge }) {
  const location = useLocation();
  const active = exact ? location.pathname === to : location.pathname.startsWith(to);
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink
            to={to}
            data-testid={testId}
            end={exact}
            className={`relative flex items-center justify-center w-12 h-12 rounded-lg transition-all ${
              active
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Icon className="h-5 w-5" />
            {badge > 0 && (
              <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </NavLink>
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function SideIconButton({ label, icon: Icon, onClick, testId, active }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            data-testid={testId}
            className={`flex items-center justify-center w-12 h-12 rounded-lg transition-all ${
              active ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Icon className="h-5 w-5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function RouteOpsLayout({ user, onLogout }) {
  const [backupOpen, setBackupOpen] = useState(false);

  // Filter the nav by which RouteOps sub-pages this user can access.
  const visibleNav = NAV_TOP.filter(item => userHasModule(user, item.moduleKey));
  const canSeeSettings = userHasModule(user, 'routeops_settings');

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="relative -mx-4 sm:-mx-6 lg:-mx-12 xl:-mx-16 -my-4 sm:-my-6 lg:-my-8 min-h-[calc(100vh-8rem)]">
        {/* RouteOps Sidebar — FIXED to viewport so it never shifts when navigating between pages */}
        <aside
          data-testid="routeops-sidebar"
          className="fixed left-0 z-30 flex flex-col items-center w-16 bg-white border-r border-slate-200 py-4 gap-2"
          style={{ top: '5rem', bottom: 0 }}
        >
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/"
                  data-testid="routeops-nav-back"
                  className="flex items-center justify-center w-12 h-12 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-all"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">Back to Fleet Dashboard</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="h-px w-8 bg-slate-200 my-1" />

          {visibleNav.map(item => (
            <SideIcon key={item.to} {...item} />
          ))}

          <div className="flex-1" />

          <SideIconButton
            label="Backup & Restore"
            icon={Database}
            onClick={() => setBackupOpen(true)}
            testId="routeops-nav-backup"
          />

          {canSeeSettings && (
            <NavLink
              to="/routeops/settings"
              data-testid="routeops-nav-settings"
              className={({ isActive }) =>
                `flex items-center justify-center w-12 h-12 rounded-lg transition-all ${
                  isActive ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                }`
              }
            >
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SettingsIcon className="h-5 w-5" />
                  </TooltipTrigger>
                  <TooltipContent side="right">Settings</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </NavLink>
          )}
        </aside>

        {/* Main RouteOps content — left margin reserves space for the fixed sidebar */}
        <main className="pl-16 min-w-0 py-4 sm:py-6 lg:py-8 pr-4 sm:pr-6 lg:pr-12 xl:pr-16">
          <div className="px-4 sm:px-6">
            <Outlet context={{ user, canEditModule: (key) => canEditModule(user, key) }} />
          </div>
        </main>
      </div>

      <BackupRestoreModal open={backupOpen} onOpenChange={setBackupOpen} />
    </Layout>
  );
}
